/**
 * Lightweight in-memory breadcrumb trail.
 *
 * A breadcrumb is a small record of "something happened" (a route change, a
 * socket status flip, a failed API call). When an error is captured we attach
 * the most recent breadcrumbs so a bare stack trace becomes "here is how the
 * user got to the crash".
 *
 * The buffer is a fixed-size ring kept in module memory only — it is never
 * persisted and is dropped on reload. Breadcrumbs must never contain message
 * plaintext, tokens, or other secrets; callers record metadata only and the
 * scrubbing layer ({@link ./scrub}) is applied again at send time as defense
 * in depth.
 */

export type BreadcrumbCategory =
  | "navigation"
  | "network"
  | "socket"
  | "auth"
  | "ui"
  | "console";

export type Breadcrumb = {
  timestamp: string;
  category: BreadcrumbCategory;
  message: string;
  data?: Record<string, string | number | boolean>;
};

const MAX_BREADCRUMBS = 30;

const buffer: Breadcrumb[] = [];

export function addBreadcrumb(
  category: BreadcrumbCategory,
  message: string,
  data?: Record<string, string | number | boolean>,
): void {
  buffer.push({
    timestamp: new Date().toISOString(),
    category,
    message,
    ...(data ? { data } : {}),
  });
  // Trim from the front so the most recent MAX_BREADCRUMBS survive.
  if (buffer.length > MAX_BREADCRUMBS) {
    buffer.splice(0, buffer.length - MAX_BREADCRUMBS);
  }
}

export function getBreadcrumbs(): Breadcrumb[] {
  // Deep copy so callers get an isolated snapshot — mutating a returned
  // breadcrumb (or its data) must not affect the internal buffer.
  return buffer.map((crumb) => ({
    ...crumb,
    ...(crumb.data ? { data: { ...crumb.data } } : {}),
  }));
}

export function clearBreadcrumbs(): void {
  buffer.length = 0;
}
